import './Styles/AdminPanelStyles.css'

import { useEffect, useMemo, useState } from 'react'
import { Nav } from 'react-bootstrap'
import { toast } from 'react-toastify'
import ErrorBoundary from '../../Components/ErrorBoundary/ErrorBoundary'
import ScrollUpArrow from '../../Components/Components/ScrollUpArrow'
import LoadingSpinner from '../../Components/Components/LoadingSpinner'
import { useConfig } from '../../Providers/ConfigProvider'
import { GetAllParameters, GetAllSiteParameters } from '../../Services/ParameterService'
import { groupParameters } from './Config/paramTypes'
import ParamGroup from './Components/ParamGroup'
import SiteParamGroup from './Components/SiteParamGroup'
import { useAuth } from '../../Providers/AuthProvider'
import NoAccess from '../../Components/NoAccess'
import MobileAccountNavBar from '../../Components/MobileComponents/NavBarMobile.jsx/MobileAccountNavBar'
import { AccountCurrentView } from '../../Data/PageModeEnums.ts'
import { useParams } from '../../Providers/ParamsProvider'
import AdminPanelNavBar from './Components/AdminPanelNavBar'
import AdminPanelSlider from './Components/AdminPanelSlider'
import AdminPanelFooter from './Components/AdminPanelFooter'

const AdminPanelPageIndex = () => {
    const { isMobile } = useParams()
    const { authState } = useAuth()
    const { api } = useConfig()
    const [activeTab, setActiveTab] = useState("system")
    const [parameters, setParameters] = useState([])
    const [siteParameters, setSiteParameters] = useState([])
    const [loading, setLoading] = useState(true)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        if (authState.user?.isOperator && authState.user?.upR_ADMIN) {
            setLoading(true)
            Promise.all([GetAllParameters(api), GetAllSiteParameters(api)])
                .then(([paramsRes, siteRes]) => {
                    setParameters(paramsRes.data)
                    setSiteParameters(siteRes.data)
                })
                .catch(() => {
                    setHasError(true)
                    toast.error('Błąd podczas ładowania parametrów.')
                })
                .finally(() => setLoading(false))
        }
    }, [api])

    const paramGroups = useMemo(() => groupParameters(parameters), [parameters])

    const siteIds = useMemo(
        () => [...new Set(siteParameters.map(param => param.siteId))].sort(),
        [siteParameters]
    )

    const siteParamGroups = useMemo(
        () => groupParameters(siteParameters.filter(param => param.siteId === activeTab)),
        [siteParameters, activeTab]
    )

    const handleParamSaved = (paramId, valueKey, newValue) => {
        setParameters(prev => prev.map(param => (
            param.paramId === paramId ? { ...param, [valueKey]: newValue } : param
        )))
    }

    const handleSiteParamSaved = (siteId, paramId, valueKey, newValue) => {
        setSiteParameters(prev => prev.map(param => (
            param.siteId === siteId && param.paramId === paramId
                ? { ...param, [valueKey]: newValue }
                : param
        )))
    }

    const getTabKind = () => {
        switch (activeTab) {
            case 'system':
                return 'system'
            case 'navBar':
                return 'navBar'
            case 'slider':
                return 'slider'
            case 'footer':
                return 'footer'
            default:
                return siteIds.includes(activeTab) ? 'site' : 'unknown'
        }
    }

    const generateTabContent = () => {
        switch (getTabKind()) {
            case 'system':
                if (paramGroups.length === 0) {
                    return <span className='text-muted'>Brak parametrów udostępnionych do edycji w eHandlu.</span>
                }
                return paramGroups.map(group => (
                    <ParamGroup
                        key={group.label}
                        group={group}
                        api={api}
                        onSaved={handleParamSaved}
                    />
                ))
            case 'navBar':
                return <AdminPanelNavBar />
            case 'slider':
                return <AdminPanelSlider />
            case 'footer':
                return <AdminPanelFooter />
            case 'site':
                if (siteParamGroups.length === 0) {
                    return <span className='text-muted'>Brak parametrów dla tego oddziału.</span>
                }
                return siteParamGroups.map(group => (
                    <SiteParamGroup
                        key={group.label}
                        group={group}
                        siteId={activeTab}
                        api={api}
                        onSaved={handleSiteParamSaved}
                    />
                ))
            default:
                return null
        }
    }

    const generateParamsForSiteTab = () => {
        if (siteIds.length === 0) {
            return null
        }
        if (siteIds.length > 1) {
            return siteIds.map(id => (
                <Nav.Item key={id}>
                    <Nav.Link eventKey={id}>Oddział {id}</Nav.Link>
                </Nav.Item>
            ))
        }
        return (
            <Nav.Item key={siteIds[0]}>
                <Nav.Link eventKey={siteIds[0]}>Parametry Systemowe Cd.</Nav.Link>
            </Nav.Item>
        )
    }

    if (!authState.user?.isOperator || !authState.user?.upR_ADMIN) {
        return <NoAccess
            isLicenseError={false}
            customErrorText={'Brak uprawnień do przeglądania panelu administracyjnego!'}
        />
    }
    return (
        <ErrorBoundary>
            <ScrollUpArrow />
            <div className='container-fluid-with-paddings mt-4 admin-panel' >
                {isMobile && <div className='mb-2'><MobileAccountNavBar currentKey={AccountCurrentView.PanelAdministratora} /></div>}
                <div className='main-container-holder'>
                    <span className='d-flex align-items-center justify-content-center primary-accent-color-text'>Po zmianie parametrów pamiętaj aby przeładowac aplikacje!</span>
                    <h2 className='mt-2 mb-4'>Panel administratora</h2>
                    {loading && <LoadingSpinner loadingText='Ładowanie parametrów...' />}
                    {!loading && !hasError && (
                        <>
                            <Nav variant='tabs' activeKey={activeTab} onSelect={setActiveTab} className='mb-4'>
                                <Nav.Item>
                                    <Nav.Link key='system' eventKey='system'>Parametry Systemowe</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link key='navBar' eventKey='navBar'>Pasek nawigacji</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link key='slider' eventKey='slider'>Slider</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link key='footer' eventKey='footer'>Stopka i benefity rejestracji</Nav.Link>
                                </Nav.Item>
                                {generateParamsForSiteTab()}
                            </Nav>
                            {generateTabContent()}
                        </>
                    )}
                </div>
            </div>
        </ErrorBoundary>
    )
}

export default AdminPanelPageIndex
